/** Cosmetic jackrabbits that hop around the arena, bouncing off the walls. */

import { ARENA_H, ARENA_W } from "@duelgame/shared";

const FRAME_W = 42;
const FRAME_H = 36;
const RADIUS_X = FRAME_W / 2;
const RADIUS_Y = FRAME_H / 2;
/** Hop travel speed range (px/s) — only applied while airborne. */
const MIN_SPEED = 55;
const MAX_SPEED = 95;
/** Seconds spent hopping before sitting down, and vice versa. */
const MIN_MOVE = 1.6;
const MAX_MOVE = 4.5;
const MIN_IDLE = 0.8;
const MAX_IDLE = 2.6;
/** Time in the air for one hop (seconds). */
const HOP_DURATION = 0.28;
/** Pause on the ground between hops (seconds). */
const HOP_GAP = 0.42;
const HOP_CYCLE = HOP_DURATION + HOP_GAP;
/** Extra lift on top of the airborne frame (px). */
const HOP_HEIGHT = 5;
const MIN_COUNT = 1;
const MAX_COUNT = 3;
const SWARM_CHANCE = 0.2;
const SWARM_COUNT = 20;

export interface JackrabbitState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  facingLeft: boolean;
  idle: boolean;
  /** Seconds left in the current idle / moving stretch. */
  timer: number;
  /** Seconds into the current hop+pause cycle. */
  phase: number;
}

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Flattened random heading so they mostly cross the arena sideways. */
function randVelocity(): { vx: number; vy: number } {
  const speed = randRange(MIN_SPEED, MAX_SPEED);
  const dir = Math.random() * Math.PI * 2;
  return { vx: Math.cos(dir) * speed, vy: Math.sin(dir) * speed * 0.5 };
}

function freshRabbit(): JackrabbitState {
  const v = randVelocity();
  return {
    x: RADIUS_X + Math.random() * (ARENA_W - FRAME_W),
    y: RADIUS_Y + Math.random() * (ARENA_H - FRAME_H),
    vx: v.vx,
    vy: v.vy,
    facingLeft: v.vx < 0,
    idle: false,
    timer: randRange(MIN_MOVE, MAX_MOVE),
    phase: Math.random() * HOP_CYCLE,
  };
}

function freshRabbits(allowSwarm = true): JackrabbitState[] {
  const count =
    allowSwarm && Math.random() < SWARM_CHANCE
      ? SWARM_COUNT
      : MIN_COUNT + Math.floor(Math.random() * (MAX_COUNT - MIN_COUNT + 1));
  const rabbits: JackrabbitState[] = [];
  for (let i = 0; i < count; i++) rabbits.push(freshRabbit());
  return rabbits;
}

function copyRabbit(s: JackrabbitState): JackrabbitState {
  return {
    x: s.x,
    y: s.y,
    vx: s.vx,
    vy: s.vy,
    facingLeft: s.facingLeft,
    idle: s.idle,
    timer: s.timer,
    phase: s.phase,
  };
}

function airborne(r: JackrabbitState): boolean {
  return !r.idle && r.phase < HOP_DURATION;
}

function bounceWalls(r: JackrabbitState): void {
  if (r.x < RADIUS_X) {
    r.x = RADIUS_X;
    r.vx = Math.abs(r.vx);
  } else if (r.x > ARENA_W - RADIUS_X) {
    r.x = ARENA_W - RADIUS_X;
    r.vx = -Math.abs(r.vx);
  }

  if (r.y < RADIUS_Y) {
    r.y = RADIUS_Y;
    r.vy = Math.abs(r.vy);
  } else if (r.y > ARENA_H - RADIUS_Y) {
    r.y = ARENA_H - RADIUS_Y;
    r.vy = -Math.abs(r.vy);
  }

  r.facingLeft = r.vx < 0;
}

function stepRabbit(r: JackrabbitState, step: number): void {
  r.timer -= step;
  if (r.timer <= 0) {
    if (r.idle) {
      const v = randVelocity();
      r.vx = v.vx;
      r.vy = v.vy;
      r.facingLeft = v.vx < 0;
      r.idle = false;
      r.timer = randRange(MIN_MOVE, MAX_MOVE);
    } else {
      r.vx = 0;
      r.vy = 0;
      r.idle = true;
      r.timer = randRange(MIN_IDLE, MAX_IDLE);
    }
    r.phase = 0;
  }

  if (r.idle) return;

  r.phase += step;
  if (r.phase >= HOP_CYCLE) r.phase -= HOP_CYCLE;

  // Only travel while airborne; sit still between hops.
  if (airborne(r)) {
    r.x += r.vx * step;
    r.y += r.vy * step;
    bounceWalls(r);
  }
}

/** Sheet frame to draw: 0 is the crouched pose, 1 is mid-hop. */
export function jackrabbitFrame(r: JackrabbitState): number {
  return airborne(r) ? 1 : 0;
}

/** Vertical offset for the hop arc (negative is up). */
export function jackrabbitHop(r: JackrabbitState): number {
  if (!airborne(r)) return 0;
  const t = r.phase / HOP_DURATION;
  return -Math.sin(t * Math.PI) * HOP_HEIGHT;
}

/** Host-authoritative jackrabbits; guest predicts and snaps to sync. */
export class JackrabbitField {
  rabbits: JackrabbitState[] = freshRabbits();

  reset(allowSwarm = true): void {
    this.rabbits = freshRabbits(allowSwarm);
  }

  update(dt: number): void {
    const step = Math.min(0.05, Math.max(0, dt));
    for (const r of this.rabbits) stepRabbit(r, step);
  }

  /** Hard snap from the host's snapshot. */
  applyRemote(list: readonly JackrabbitState[]): void {
    if (this.rabbits.length !== list.length) {
      this.rabbits = list.map(copyRabbit);
      return;
    }
    for (let i = 0; i < list.length; i++) {
      const r = this.rabbits[i]!;
      const s = list[i]!;
      r.x = s.x;
      r.y = s.y;
      r.vx = s.vx;
      r.vy = s.vy;
      r.facingLeft = s.facingLeft;
      r.idle = s.idle;
      r.timer = s.timer;
      r.phase = s.phase;
    }
  }

  snapshot(): JackrabbitState[] {
    return this.rabbits.map(copyRabbit);
  }
}

export const JACKRABBIT_FRAME_W = FRAME_W;
export const JACKRABBIT_FRAME_H = FRAME_H;
